import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Upload, Check } from "lucide-react";
import { Reveal, SectionLabel } from "../components/primitives";
import { REQUIREMENT_TYPES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">
      {label} {required && <span className="text-[#d99e12]">*</span>}
    </span>
    {children}
  </label>
);

const input =
  "w-full bg-transparent border-0 border-b border-neutral-300 focus:border-neutral-900 outline-none py-3 text-lg text-neutral-900 placeholder:text-neutral-400 transition-colors";

const Inquire = () => {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    full_name: "", company_name: "", work_email: "", phone: "", designation: "",
    requirement_type: "", quantity: "", delivery_date: "", delivery_location: "",
    budget_range: "", message: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.company_name || !form.work_email || !form.phone || !form.requirement_type || !form.message) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("reference_file", file);
      await axios.post(`${API}/inquiries`, fd);
      nav("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="inquire-page" className="pt-[120px] md:pt-[150px] pb-28">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <SectionLabel>Inquire</SectionLabel>
              <Reveal>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02]">
                  Let's build something for your business.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg text-neutral-600 max-w-md">
                  Tell us what you need. Our team will get back to you with the right solution — routed to the right specialists.
                </p>
              </Reveal>
              <div className="mt-10 space-y-3">
                {["One partner for every requirement", "Pan-India delivery across 25+ states", "Access to 100+ brands & 400+ voucher brands"].map((t) => (
                  <div key={t} className="flex items-center gap-3 text-neutral-700">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-[#f4b41a] shrink-0"><Check size={14} className="text-neutral-900" /></span>
                    {t}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="inquiry-form" className="space-y-12">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mb-6 font-semibold">Contact Information</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                  <Field label="Full Name" required><input data-testid="inq-full-name" className={input} value={form.full_name} onChange={set("full_name")} placeholder="Your name" /></Field>
                  <Field label="Company Name" required><input data-testid="inq-company" className={input} value={form.company_name} onChange={set("company_name")} placeholder="Company" /></Field>
                  <Field label="Work Email" required><input data-testid="inq-email" type="email" className={input} value={form.work_email} onChange={set("work_email")} placeholder="name@company.com" /></Field>
                  <Field label="Phone Number" required><input data-testid="inq-phone" className={input} value={form.phone} onChange={set("phone")} placeholder="+91" /></Field>
                  <Field label="Designation"><input data-testid="inq-designation" className={input} value={form.designation} onChange={set("designation")} placeholder="Optional" /></Field>
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-neutral-400 mb-6 font-semibold">Requirement</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                  <Field label="What are you looking for?" required>
                    <select data-testid="inq-type" className={`${input} cursor-pointer`} value={form.requirement_type} onChange={set("requirement_type")}>
                      <option value="">Select a category</option>
                      {REQUIREMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Approximate Quantity"><input data-testid="inq-qty" className={input} value={form.quantity} onChange={set("quantity")} placeholder="e.g. 500 units" /></Field>
                  <Field label="Expected Delivery Date"><input data-testid="inq-date" type="date" className={`${input} cursor-pointer`} value={form.delivery_date} onChange={set("delivery_date")} /></Field>
                  <Field label="Delivery Location"><input data-testid="inq-location" className={input} value={form.delivery_location} onChange={set("delivery_location")} placeholder="City / Pan-India" /></Field>
                  <Field label="Budget Range"><input data-testid="inq-budget" className={input} value={form.budget_range} onChange={set("budget_range")} placeholder="Optional" /></Field>
                </div>
                <div className="mt-7">
                  <Field label="Tell us about your requirement" required>
                    <textarea data-testid="inq-message" rows={4} className={`${input} resize-none`} value={form.message} onChange={set("message")} placeholder="Describe what you're looking for…" />
                  </Field>
                </div>
                <div className="mt-7">
                  <span className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Upload Reference File</span>
                  <label className="flex items-center gap-3 rounded-xl border border-dashed border-neutral-300 hover:border-neutral-900 px-5 py-5 cursor-pointer transition-colors">
                    <Upload size={18} className="text-neutral-500" />
                    <span className="text-neutral-600 text-sm">{fileName || "Attach a requirement document (PDF, image, doc)"}</span>
                    <input type="file" data-testid="inq-file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); setFileName(f?.name || ""); }} />
                  </label>
                </div>
              </div>

              <button
                data-testid="inq-submit"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white font-semibold px-8 py-4 hover:bg-[#f4b41a] hover:text-neutral-900 transition-colors disabled:opacity-60"
              >
                {loading ? "Submitting…" : "Submit Requirement"}
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Inquire;
